import { Object as FabricObject } from 'fabric/fabric-impl';
import { MediaManagerTemplateFilter } from 'modules/core/domain/interfaces/supabase/media-manager/actions';
import {
  MediaManagerRenderedImage,
  MediaManagerTemplate,
} from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import { MatchLayoutSettings } from 'modules/manager/domain/match-layout';
import supabase from 'modules/core/supabase/supabase';

export interface UpdateTemplateObjectsInput {
  id: number;
  objects: FabricObject[];
  version: string;
}

export interface UpdateTemplateMatchLayoutInput {
  id: number;
  matchLayout: MatchLayoutSettings;
}

export interface PersistRenderedImageInput {
  matchId: number;
  templateId: number;
  dataUrl: string;
}

export interface StageRenderedImageInput {
  /** '' for a non-GROUP stage's single table, team-group id (as text) for a GROUP stage. */
  groupKey: string;
  groupId?: number;
  groupName?: string;
  dataUrl: string;
}

export interface PersistStageRenderedImagesInput {
  stageId: number;
  templateId: number;
  images: StageRenderedImageInput[];
}

export class MediaManagerRepository {
  getTemplates(filter?: MediaManagerTemplateFilter): Promise<MediaManagerTemplate[]> {
    return supabase.mediaManager.getTemplates(filter);
  }

  getTemplateById(id: number): Promise<MediaManagerTemplate> {
    return supabase.mediaManager.getTemplateById(id);
  }

  updateTemplateObjects(input: UpdateTemplateObjectsInput): Promise<MediaManagerTemplate> {
    return supabase.mediaManager.updateTemplate({
      id: input.id,
      objects: input.objects,
      version: input.version,
    });
  }

  updateTemplateMatchLayout(input: UpdateTemplateMatchLayoutInput): Promise<MediaManagerTemplate> {
    return supabase.mediaManager.updateTemplate({
      id: input.id,
      matchLayout: input.matchLayout,
    });
  }

  async persistRenderedImage(input: PersistRenderedImageInput): Promise<MediaManagerRenderedImage> {
    const previous = await supabase.mediaManagerRenderedImages.getByMatchAndTemplate(
      input.matchId,
      input.templateId,
    );
    const previousImageUrl = previous?.imageUrl ?? null;

    const { publicUrl } = await supabase.renderedImagesStorage.upload(
      input.matchId,
      input.templateId,
      input.dataUrl,
    );

    let result: MediaManagerRenderedImage;
    try {
      result = await supabase.mediaManagerRenderedImages.upsert({
        matchId: input.matchId,
        templateId: input.templateId,
        imageUrl: publicUrl,
      });
    } catch (err) {
      await supabase.renderedImagesStorage.remove(publicUrl).catch((cleanupErr) => {
        console.error('[render] storage rollback failed', cleanupErr);
      });
      throw err;
    }

    if (previousImageUrl && previousImageUrl !== publicUrl) {
      await supabase.renderedImagesStorage.remove(previousImageUrl).catch((cleanupErr) => {
        console.error('[render] previous image cleanup failed', cleanupErr);
      });
    }

    return result;
  }

  async deleteRenderedImage(id: string): Promise<void> {
    const row = await supabase.mediaManagerRenderedImages.getById(id);
    if (!row) return;

    await supabase.renderedImagesStorage.remove(row.imageUrl);
    await supabase.mediaManagerRenderedImages.deleteById(id);
  }

  /**
   * Persists one image per standings target (one per team group for a GROUP
   * stage, one image for any other stage type) and reconciles the stored set
   * against the current backend groups: stale rows (groups that disappeared
   * since the last render) are removed, so re-rendering a stage always
   * leaves storage matching its current groups exactly.
   */
  async persistStageRenderedImages(
    input: PersistStageRenderedImagesInput,
  ): Promise<MediaManagerRenderedImage[]> {
    const existing = await supabase.mediaManagerRenderedImages.getByStageAndTemplate(
      input.stageId,
      input.templateId,
    );
    const existingByGroupKey = new Map(existing.map((row) => [row.groupKey, row]));
    const newGroupKeys = new Set(input.images.map((img) => img.groupKey));

    const results: MediaManagerRenderedImage[] = [];
    for (const img of input.images) {
      const previous = existingByGroupKey.get(img.groupKey);
      const { publicUrl } = await supabase.renderedImagesStorage.uploadStanding(
        input.stageId,
        input.templateId,
        img.groupKey,
        img.dataUrl,
      );

      let result: MediaManagerRenderedImage;
      try {
        result = await supabase.mediaManagerRenderedImages.upsertStage({
          stageId: input.stageId,
          templateId: input.templateId,
          groupKey: img.groupKey,
          groupId: img.groupId,
          groupName: img.groupName,
          imageUrl: publicUrl,
        });
      } catch (err) {
        await supabase.renderedImagesStorage.remove(publicUrl).catch((cleanupErr) => {
          console.error('[render] standings storage rollback failed', cleanupErr);
        });
        throw err;
      }

      if (previous && previous.imageUrl !== publicUrl) {
        await supabase.renderedImagesStorage.remove(previous.imageUrl).catch((cleanupErr) => {
          console.error('[render] previous standings image cleanup failed', cleanupErr);
        });
      }

      results.push(result);
    }

    const stale = existing.filter((row) => !newGroupKeys.has(row.groupKey));
    for (const row of stale) {
      await supabase.renderedImagesStorage.remove(row.imageUrl).catch((cleanupErr) => {
        console.error('[render] stale standings image cleanup failed', cleanupErr);
      });
      await supabase.mediaManagerRenderedImages.deleteById(row.id);
    }

    return results;
  }
}

const mediaManagerRepository = new MediaManagerRepository();
export default mediaManagerRepository;
